import {
  addDoc,
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  type DocumentData,
  type FieldValue,
  type Unsubscribe,
  type UpdateData,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export const CALL_SESSIONS = 'callSessions';
export const ICE_CANDIDATES = 'iceCandidates';

export type CallKind = 'audio' | 'video';

export type CallSessionState = 'ringing' | 'active' | 'rejected' | 'ended' | 'failed';

export type CallSessionDoc = {
  initiatorUid: string;
  targetUid: string;
  kind: CallKind;
  state: CallSessionState;
  createdAt: unknown;
  offerSdp?: string;
  answerSdp?: string;
  endedAt?: unknown;
};

function sessionsRef(threadId: string) {
  return collection(db, 'p2pChatThreads', threadId, CALL_SESSIONS);
}

function iceRef(threadId: string, sessionId: string) {
  return collection(db, 'p2pChatThreads', threadId, CALL_SESSIONS, sessionId, ICE_CANDIDATES);
}

export function subscribeCallSessions(
  threadId: string,
  onDocs: (list: { id: string; data: CallSessionDoc }[]) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const q = query(sessionsRef(threadId), orderBy('createdAt', 'desc'), limit(20));
  return onSnapshot(
    q,
    (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, data: d.data() as CallSessionDoc }));
      onDocs(list);
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
  );
}

export function subscribeCallSessionDoc(
  threadId: string,
  sessionId: string,
  onData: (data: CallSessionDoc | null) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const r = doc(db, 'p2pChatThreads', threadId, CALL_SESSIONS, sessionId);
  return onSnapshot(
    r,
    (snap) => {
      onData(snap.exists() ? (snap.data() as CallSessionDoc) : null);
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
  );
}

export function subscribeIceCandidates(
  threadId: string,
  sessionId: string,
  myUid: string,
  onCandidate: (init: RTCIceCandidateInit) => void,
  onError?: (e: Error) => void
): Unsubscribe {
  const q = query(iceRef(threadId, sessionId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => {
      for (const ch of snap.docChanges()) {
        if (ch.type !== 'added') continue;
        const row = ch.doc.data() as DocumentData;
        if (row.fromUid === myUid) continue;
        try {
          const parsed = JSON.parse(String(row.payload ?? '')) as RTCIceCandidateInit;
          if (parsed && typeof parsed.candidate === 'string') onCandidate(parsed);
        } catch {
          /* ignore */
        }
      }
    },
    (err) => onError?.(err instanceof Error ? err : new Error(String(err)))
  );
}

export async function createRingingSession(params: {
  threadId: string;
  initiatorUid: string;
  targetUid: string;
  kind: CallKind;
}): Promise<string> {
  const ref = await addDoc(sessionsRef(params.threadId), {
    initiatorUid: params.initiatorUid,
    targetUid: params.targetUid,
    kind: params.kind,
    state: 'ringing' as CallSessionState,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateCallSession(
  threadId: string,
  sessionId: string,
  patch: Partial<Pick<CallSessionDoc, 'offerSdp' | 'answerSdp' | 'state'>> & {
    endedAt?: FieldValue | Date;
  }
): Promise<void> {
  const r = doc(db, 'p2pChatThreads', threadId, CALL_SESSIONS, sessionId);
  await updateDoc(r, patch as UpdateData<DocumentData>);
}

export async function pushIceCandidate(params: {
  threadId: string;
  sessionId: string;
  fromUid: string;
  candidate: RTCIceCandidateInit;
}): Promise<void> {
  await addDoc(iceRef(params.threadId, params.sessionId), {
    fromUid: params.fromUid,
    payload: JSON.stringify(params.candidate),
    createdAt: serverTimestamp(),
  });
}
